import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "input": {
 *     "prompt": "一只可爱的橘猫在阳光下睡觉",
 *     "negative_prompt": "模糊，低质量"
 *   },
 *   "parameters": {
 *     "size": "1024*1024",
 *     "n": 1,
 *     "seed": 42,
 *     "prompt_extend": true,
 *     "watermark": false
 *   }
 * }
 * ```
 */
export interface AlibabaWan22T2iFlashParams {
  /**
   * 输入参数，包含提示词信息
   */
  input: {
    /**
     * 正向提示词，描述想要生成的图片内容，长度不超过2000字符
     */
    prompt: string;
    /**
     * 负向提示词，描述不希望出现的内容，长度不超过500字符（可选）
     */
    negative_prompt?: string;
  };
  /**
   * 生成参数（可选）
   */
  parameters?: {
    /**
     * 图像尺寸，格式为'宽*高'（如'1024*768'），宽高必须在[512, 1440]范围内，且最大分辨率不超过1440*1440
     */
    size?: string;
    /**
     * 生成图像数量，范围1-4，默认1张
     * @range 1 - 4
     */
    n?: number;
    /**
     * 随机种子，用于确保结果可复现，范围0-2147483647
     * @range 0 - 2147483647
     */
    seed?: number;
    /**
     * 是否启用提示词扩展功能，启用后会自动优化和丰富提示词
     */
    prompt_extend?: boolean;
    /**
     * 是否在生成的图片上添加水印
     */
    watermark?: boolean;
  };
}

export interface AlibabaWan22T2iFlashResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * alibaba_wan22_t2i_flash
 *
 * Alibaba Wanxiang 2.2 T2I Flash 文本生成图片接口，支持快速生成高质量图片。
 * 
 * Model: wan2.2-t2i-flash
 * Features:
 * - 支持自定义图片尺寸（宽高范围512-1440像素）
 * - 支持批量生成（1-4张）
 * - 支持固定随机种子以确保结果可复现
 * - 支持提示词扩展功能
 * - 支持水印添加选项
 * - 异步任务处理，自动轮询获取结果
 *
 * @example
 * ```typescript
 * import { initSeacloud, alibabaWan22T2iFlash } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await alibabaWan22T2iFlash(
 *   {
 *     "input": {
 *       "prompt": "一只可爱的橘猫在阳光下睡觉",
 *       "negative_prompt": "模糊，低质量"
 *     },
 *     "parameters": {
 *       "size": "1024*1024",
 *       "n": 1,
 *       "seed": 42,
 *       "prompt_extend": true,
 *       "watermark": false
 *     }
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 AlibabaWan22T2iFlashParams 接口定义
 * @returns 生成的资源列表
 */
export async function alibabaWan22T2iFlash(
  params: AlibabaWan22T2iFlashParams,
): Promise<AlibabaWan22T2iFlashResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'alibaba_wan22_t2i_flash',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: AlibabaWan22T2iFlashResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
